import { useContext, useEffect } from 'react';
import { SmartContext } from '../Core/SmartContext';
import { getControlValueFromState } from '../Core/SmartFunctions';
import { SimpleFormControlArguments, State } from '../Core/SmartTypes';
import TextControl from './TextControl';
import { Tooltip } from 'bootstrap';

const SocialNetworkControl = (args: SimpleFormControlArguments) => {
    const { state, dispatch } = useContext(SmartContext);
    const { control, parentDataKey } = args;

    const data =
        getControlValueFromState(parentDataKey as string, state as State) || [];

    const SOCIAL_ORDER = [
        'LINKEDIN',
        'FACEBOOK',
        'INSTAGRAM',
        'TWITTER',
        'OTHER',
    ];

    const icons: any = {
        linkedin: 'bi bi-linkedin text-linkedin font-20',
        facebook: 'bi bi-facebook text-facebook font-20',
        instagram: 'bi bi-instagram text-instagram font-20',
        twitter: 'bi bi-twitter-x text-dark font-20',
        'other': 'bi bi-globe text-linkedin font-20',
    };

    const placeholders: any = {
        LINKEDIN: 'https://www.linkedin.com/in/your-profile',
        FACEBOOK: 'Enter your Facebook profile URL',
        INSTAGRAM: 'Enter your Instagram profile URL',
        TWITTER: 'Enter your X (Twitter) profile URL',
        OTHER: 'Enter your profile URL',
    };

    const getIconClass = (code: string) => {
        if (!code) return 'bi bi-linkedin';

        return (
            icons[
                code.toLowerCase().replace('_', '')
            ] || 'bi bi-linkedin'
        );
    };

    const getTooltipContent = (code: string) => {
        const upperCode = (code || '').toUpperCase();

        switch (upperCode) {
            case 'LINKEDIN':
                return `
                    <div class="text-start fw-bold">How to find your URL?</div>
                    <ol class="text-start ps-3 mb-1">
                        <li>Click the Me icon at the top of your LinkedIn homepage.</li>
                        <li>Click View Profile.</li>
                        <li>Copy the URL from your browser's address bar (it looks like linkedin.com/in/yourname).</li>
                    </ol>
                    <p class="text-start mb-0">
                        A public profile helps batchmates and recruiters find you easily.
                    </p>
                `;

            case 'FACEBOOK':
                return `
                    <div class="text-start fw-bold">How to find your URL?</div>
                    <ol class="text-start ps-3 mb-1">
                        <li>Click on your profile picture at the top right of Facebook.</li>
                        <li>Click your name to open your profile page.</li>
                        <li>Copy the URL from your browser's address bar (it looks like facebook.com/yourname).</li>
                    </ol>
                `;

            case 'INSTAGRAM':
                return `
                    <div class="text-start fw-bold">How to find your URL?</div>
                    <ol class="text-start ps-3 mb-1">
                        <li>Open Instagram in a web browser and log in.</li>
                        <li>Click your profile picture, then click "Profile".</li>
                        <li>Copy the URL from your browser's address bar (it looks like instagram.com/yourname).</li>
                    </ol>
                    <p class="text-start mb-0">
                        If using the mobile app, your profile URL is instagram.com/ followed by your username.
                    </p>
                `;

            case 'TWITTER':
                return `
                    <div class="text-start fw-bold">How to find your URL?</div>
                    <ol class="text-start ps-3 mb-1">
                        <li>Click on your profile picture in the left sidebar.</li>
                        <li>Click "Profile" to open your profile page.</li>
                        <li>Copy the URL from your browser's address bar (it looks like x.com/yourname).</li>
                    </ol>
                `;

            default:
                return `
                    <div class="text-start fw-bold">How to find your URL?</div>
                    <ol class="text-start ps-3 mb-1">
                        <li>Log in to the platform and navigate to your profile.</li>
                        <li>Click options like "View Profile" or "My Profile".</li>
                        <li>Copy the complete URL from your browser's address bar.</li>
                    </ol>
                    <p class="text-start mb-0">
                        Make sure the URL shows your public profile, not a settings or edit page.
                    </p>
                `;
        }
    };

    const handleAddMore = () => {
        if (data.length >= SOCIAL_ORDER.length) return;

        const nextNetwork = SOCIAL_ORDER[data.length];

        dispatch({
            type: 'CONTROL_VALUE_CHANGE',
            payload: {
                dataKey: parentDataKey,
                value: [
                    ...data,
                    {
                        socialNetworkCode: nextNetwork,
                        link: ''
                    }
                ],
            },
        });
    };

    // Initialize Bootstrap tooltip
    useEffect(() => {
        const tooltipTriggerList = document.querySelectorAll(
            '[data-bs-toggle="tooltip"]'
        );

        tooltipTriggerList.forEach((el: any) => {
            const existing = Tooltip.getInstance(el);
            if (existing) existing.dispose();

            new Tooltip(el, {
                trigger: 'hover',
                html: true,
                placement: 'right',
            });
        });
    }, [data]);

    return (
        <>
            <div className='font-18 font-500 mb-3'>                
                Share your Professional Profile or Portfolio URL.
                <span className='font-16 font-400 d-block'>We don't share it with third party.</span>
            </div>
            <label className="form-label w-100">
                {control.props.label}
            </label>

            {data.map((row: any, index: number) => {
                const linkControl = control.controlGroup.find(
                    (ctrl) => ctrl.id === 'link'
                )!;

                const updatedLinkControl = {
                    ...linkControl,
                    props: {
                        ...linkControl.props,
                        placeholder:
                            placeholders[row?.socialNetworkCode] ||
                            'Enter profile link',
                    },
                };

                return (
                    <div key={index} className="mb-3 position-relative">
                        <div className="input-group">

                            <span className="input-group-text">
                                <i className={getIconClass(row?.socialNetworkCode)}></i>
                            </span>

                            <TextControl
                                control={updatedLinkControl}
                                dataKey={`${parentDataKey}.${index}.link`}
                                parentDataKey={parentDataKey}
                            />

                            <span
                                className="input-group-text d-flex align-items-center"
                                style={{ cursor: 'pointer' }}
                                data-bs-toggle="tooltip"
                                data-bs-html="true"
                                data-bs-placement="right"
                                data-bs-title={getTooltipContent(row?.socialNetworkCode)}
                            >
                                <i className="bi bi-info-circle text-secondary fs-5"></i>
                            </span>

                        </div>
                    </div>
                );
            })}

            {data.length < SOCIAL_ORDER.length && (
                <button
                    type="button"
                    className="btn btn-link p-0 mt-2"
                    onClick={handleAddMore}
                >
                    <i className="bi bi-plus-circle"></i> Add More
                </button>
            )}
        </>
    );
};

export default SocialNetworkControl;
